'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { ArrowRight, Cloud, Cpu, Monitor, Users } from 'lucide-react';
import { useCadrinoCopy } from '@/lib/cadrino-landing/useCadrinoCopy';
import { fadeUp, stagger } from '@/lib/animations';
import { Container, Section, SectionHeader, Card, MotionBlock } from './shared';
const nodes = [{
  key: 'terminal' as const,
  icon: Monitor
}, {
  key: 'worker' as const,
  icon: Cpu
}, {
  key: 'cloud' as const,
  icon: Cloud
}, {
  key: 'hr' as const,
  icon: Users
}];
export function SolutionSection() {
  const $ = _c(16);
  const c = useCadrinoCopy();
  const arch = c.solution.architecture;
  let t0;
  if ($[0] !== c.solution.subtitle || $[1] !== c.solution.title) {
    t0 = <MotionBlock><SectionHeader title={c.solution.title} subtitle={c.solution.subtitle} /></MotionBlock>;
    $[0] = c.solution.subtitle;
    $[1] = c.solution.title;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      once: true
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== arch) {
    t2 = nodes.map((node, i) => {
      const Icon = node.icon;
      const label = node.key === "terminal" ? arch.terminal : node.key === "worker" ? arch.worker : node.key === "cloud" ? arch.cloud : arch.hr;
      return <div key={node.key} className="flex flex-1 items-center gap-4"><motion.div variants={fadeUp} className="flex flex-1 flex-col items-center text-center"><div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/[0.08] bg-cadrino-bg"><Icon className="h-6 w-6 text-cadrino-primary" /></div><p className="mt-3 text-sm font-medium text-slate-200">{label}</p></motion.div>{i < nodes.length - 1 && <ArrowRight className="hidden h-4 w-4 shrink-0 text-cadrino-muted md:block" />}</div>;
    });
    $[4] = arch;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <MotionBlock className="mt-12"><Card className="overflow-hidden p-8 md:p-12"><motion.div className="flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center" variants={stagger} initial="hidden" whileInView="visible" viewport={t1}>{t2}</motion.div></Card></MotionBlock>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== c.solution.pillars) {
    t4 = c.solution.pillars.map(_temp);
    $[8] = c.solution.pillars;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{t4}</div>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t0 || $[13] !== t3 || $[14] !== t5) {
    t6 = <Section id="solution" className="border-y border-white/[0.08] bg-cadrino-surface/40"><Container>{t0}{t3}{t5}</Container></Section>;
    $[12] = t0;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp(p, i_0) {
  return <MotionBlock key={p.title} delay={i_0 * 0.05}><Card className="h-full"><h3 className="text-sm font-semibold text-slate-100">{p.title}</h3><p className="mt-2 text-sm leading-relaxed text-cadrino-muted">{p.description}</p></Card></MotionBlock>;
}